import { Injectable, NgZone, inject } from "@angular/core";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from "@angular/router";
import { Subscription } from "rxjs";
import { AuthService } from "../services/auth.service";
import { SENTRY_CLIENT } from "../services/logger.service";
import { UserService } from "../services/user.service";
import { withdrawalDiagnosticRoute } from "../utils/withdrawal-diagnostic-route";
import {
  withdrawalJournal,
  type JournalFailurePhase,
} from "./withdrawal-journal";

const path = (url: string): string =>
  withdrawalDiagnosticRoute(url).slice(0, 500);

@Injectable({ providedIn: "root" })
export class WithdrawalJournalInitializer {
  private readonly auth = inject(AuthService);
  private readonly users = inject(UserService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly sentry = inject(SENTRY_CLIENT);
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.safely("initialize", () =>
      this.zone.runOutsideAngular(() => {
        let uid: string | null = null;
        const currentUid = () => uid;
        let tokenSubscription: Subscription | undefined;
        let tokenUid: string | null = null;
        const applyProfile = () => {
          try {
            const profile = this.users.userProfileSubject.value?.user;
            if (uid && profile?.userId === uid)
              withdrawalJournal.setUser(uid, profile.country);
          } catch {
            withdrawalJournal.reportFailure("profile");
          }
        };
        try {
          this.auth.getFireAuthUser().subscribe({
            next: (user) =>
              this.safely("auth", () => {
                uid = user?.uid ?? null;
                withdrawalJournal.setAuthenticatedUser(uid);
                applyProfile();
                if (
                  tokenUid === uid &&
                  tokenSubscription &&
                  !tokenSubscription.closed
                )
                  return;
                tokenSubscription?.unsubscribe();
                tokenUid = uid;
                if (!uid) {
                  withdrawalJournal.setToken(null);
                  return;
                }
                tokenSubscription = this.subscribeToken(uid, currentUid);
              }),
            error: () =>
              this.safely("auth", () => {
                withdrawalJournal.reportFailure("auth");
                uid = null;
                tokenSubscription?.unsubscribe();
                withdrawalJournal.setAuthenticatedUser(null);
              }),
          });
          this.users.userProfileSubject.subscribe({
            next: applyProfile,
            error: () => withdrawalJournal.reportFailure("profile"),
          });
        } catch {
          withdrawalJournal.reportFailure("auth");
          this.safely("token", () => withdrawalJournal.setToken(null));
        }
        withdrawalJournal.subscribeRecords((record) => {
          try {
            this.sentry.addBreadcrumb({
              category: "withdrawal-journal",
              message: record.event,
              level: record.level,
              data: {
                ...record.tags,
                ...record.extra,
                record_id: record.id,
                page_load_id: record.pageLoadId,
                seq: record.seq,
              },
            });
          } catch {
            withdrawalJournal.reportFailure("breadcrumb");
          }
        });
        this.router.events.subscribe({
          next: (event) => {
            try {
              if (!withdrawalJournal.isEnabled()) return;
              const currentRoute = path(this.router.url);
              const targetRoute = "url" in event ? path(event.url) : "";
              if (
                ![currentRoute, targetRoute].some(
                  (route) =>
                    route.startsWith("/main/withdraw/") ||
                    route === "/main/advance"
                )
              )
                return;
              const tags = {
                current_route: currentRoute,
                target_route: targetRoute,
              };
              if (event instanceof NavigationStart)
                withdrawalJournal.record("withdrawal.route_requested", {
                  tags,
                });
              else if (event instanceof NavigationEnd)
                withdrawalJournal.record("withdrawal.route_completed", {
                  tags: {
                    ...tags,
                    current_route: path(event.urlAfterRedirects),
                  },
                });
              else if (event instanceof NavigationCancel)
                withdrawalJournal.record("withdrawal.route_cancelled", {
                  level: "warning",
                  tags,
                });
              else if (event instanceof NavigationError)
                withdrawalJournal.record("withdrawal.route_failed", {
                  level: "warning",
                  tags,
                });
            } catch {
              withdrawalJournal.reportFailure("router");
            }
          },
          error: () => withdrawalJournal.reportFailure("router"),
        });
      })
    );
  }

  private subscribeToken(
    expectedUid: string,
    currentUid: () => string | null
  ): Subscription {
    return this.auth.getIdTokenResult().subscribe({
      next: (result) =>
        this.safely("token", () => {
          if (currentUid() !== expectedUid) return;
          const subject = result?.claims?.sub ?? result?.claims?.user_id;
          if (!subject || !result?.token) {
            withdrawalJournal.setToken(null);
            return;
          }
          if (subject === expectedUid)
            withdrawalJournal.setToken(result.token, subject);
        }),
      error: () =>
        this.safely("token", () => {
          withdrawalJournal.reportFailure("token");
          if (currentUid() === expectedUid) withdrawalJournal.setToken(null);
        }),
    });
  }

  private safely(phase: JournalFailurePhase, callback: () => void): void {
    try {
      callback();
    } catch {
      withdrawalJournal.reportFailure(phase);
    }
  }
}

export function initializeWithdrawalJournal(
  initializer: WithdrawalJournalInitializer
): () => void {
  return () => {
    try {
      initializer.start();
    } catch {
      withdrawalJournal.reportFailure("initialize");
    }
  };
}
